import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Buildings, Users, CurrencyEur, Calendar, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { supabase, offerCurrentPrice } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

const SuperAdminDashboard = () => {
  const { authUser } = useAuth();
  const [tab, setTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    const [u, b, o, bk] = await Promise.all([
      supabase.from("users").select("*").order("created_at", { ascending: false }).limit(500),
      supabase.from("businesses").select("*").limit(200),
      supabase.from("offers").select("id, title, status, original_price, offer_price, business_id").limit(500),
      supabase.from("bookings").select("id, status, payment:payments(amount)").limit(500),
    ]);
    setUsers(u.data || []);
    setBusinesses(b.data || []);
    setOffers(o.data || []);
    setBookings(bk.data || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const updateUserRole = async (id, role) => {
    const { error } = await supabase.from("users").update({ role }).eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Rol actualizado"); reload(); }
  };

  const deleteUserProfile = async (id, email) => {
    if (id === authUser?.id) { toast.error("No puedes borrar tu propio perfil"); return; }
    if (!window.confirm(`¿Eliminar el profile de ${email}? El usuario seguirá existiendo en Supabase Auth, solo se borra la fila de public.users (perderá su rol y sus reservas pasadas).`)) return;
    const { error } = await supabase.from("users").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Profile eliminado"); reload(); }
  };

  const removeBusiness = async (id) => {
    if (!window.confirm("¿Eliminar business? Esto puede afectar a ofertas existentes.")) return;
    const { error } = await supabase.from("businesses").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Eliminado"); reload(); }
  };

  const revenue = bookings
    .filter((b) => b.status === "confirmed" || b.status === "used")
    .reduce((s, b) => s + Number(b.payment?.[0]?.amount ?? 0), 0);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12" data-testid="super-admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
        <span className="text-[11px] uppercase tracking-[0.3em] text-sage-deep block mb-2">/ super admin</span>
        <h1 className="font-heading text-4xl md:text-5xl font-light text-forest">Centro de control</h1>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
        <Stat icon={<Users size={18} />} l="Usuarios" v={users.length} />
        <Stat icon={<Buildings size={18} />} l="Businesses" v={businesses.length} />
        <Stat icon={<Calendar size={18} />} l="Ofertas" v={offers.length} />
        <Stat icon={<Calendar size={18} />} l="Reservas" v={bookings.length} />
        <Stat icon={<CurrencyEur size={18} />} l="Ingresos" v={`${revenue.toFixed(2)}€`} />
      </div>

      <div className="flex gap-2 mb-6 border-b border-forest/10">
        {[
          { v: "overview", l: "Resumen" },
          { v: "users", l: "Usuarios" },
          { v: "businesses", l: "Businesses" },
        ].map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`tab-${t.v}`}
            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition ${tab === t.v ? "border-sage-deep text-sage-deep" : "border-transparent text-stone hover:text-forest"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {loading ? <p className="text-stone text-sm">Cargando…</p> : (<>
        {tab === "overview" && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-forest/10 bg-white rounded-2xl p-6">
              <h3 className="font-heading text-xl font-semibold mb-4 text-forest">Businesses</h3>
              <div className="space-y-2">
                {businesses.slice(0, 5).map((c) => (
                  <div key={c.id} className="flex justify-between text-sm py-2 border-b border-forest/5">
                    <span className="text-forest">{c.name}</span>
                    <span className="text-stone">{c.category}</span>
                  </div>
                ))}
                {businesses.length === 0 && <p className="text-stone text-sm">Sin businesses.</p>}
              </div>
            </div>
            <div className="border border-forest/10 bg-white rounded-2xl p-6">
              <h3 className="font-heading text-xl font-semibold mb-4 text-forest">Últimos usuarios</h3>
              <div className="space-y-2">
                {users.slice(0, 5).map((u) => (
                  <div key={u.id} className="flex justify-between text-sm py-2 border-b border-forest/5">
                    <span className="text-forest truncate">{u.email}</span>
                    <span className="text-sage-deep text-xs uppercase tracking-wider">{u.role}</span>
                  </div>
                ))}
                {users.length === 0 && <p className="text-stone text-sm">Sin usuarios.</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="border border-forest/10 bg-white rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <div className="font-medium text-forest">{u.email}</div>
                  <div className="text-xs text-stone">{u.telefono || "sin teléfono"} · <span className="font-mono">{u.id?.slice(0, 8)}…</span></div>
                </div>
                <select data-testid={`role-${u.id}`} value={u.role || "user"} onChange={(e) => updateUserRole(u.id, e.target.value)}
                  className="bg-mist border border-forest/15 px-3 py-2 text-sm focus:border-sage-deep focus:outline-none rounded-md">
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                  <option value="superadmin">superadmin</option>
                </select>
                <button onClick={() => deleteUserProfile(u.id, u.email)} data-testid={`del-user-${u.id}`}
                  disabled={u.id === authUser?.id}
                  className="p-2 border border-forest/15 hover:border-red-500 hover:text-red-500 rounded-md transition disabled:opacity-30 disabled:cursor-not-allowed"
                  title={u.id === authUser?.id ? "No puedes borrar tu propio perfil" : "Eliminar profile"}>
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "businesses" && (
          <div className="space-y-2">
            {businesses.map((c) => (
              <div key={c.id} className="border border-forest/10 bg-white rounded-xl p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="flex-1">
                  <div className="font-medium text-forest">{c.name}</div>
                  <div className="text-sm text-stone">{c.category} · {c.address || "sin dirección"}</div>
                </div>
                <button onClick={() => removeBusiness(c.id)} data-testid={`del-biz-${c.id}`} className="p-2 border border-forest/15 hover:border-red-500 hover:text-red-500 rounded-md transition"><Trash size={16} /></button>
              </div>
            ))}
            {businesses.length === 0 && <p className="text-stone text-sm py-8">Sin businesses. Créalos desde Supabase Dashboard (Table Editor).</p>}
          </div>
        )}
      </>)}
    </div>
  );
};

const Stat = ({ icon, l, v }) => (
  <div className="border border-forest/10 bg-white rounded-2xl p-5">
    <div className="text-sage-deep mb-2">{icon}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-stone">{l}</div>
    <div className="font-heading text-2xl font-semibold mt-1 text-forest">{v ?? "—"}</div>
  </div>
);

export default SuperAdminDashboard;
